import { Helmet } from 'react-helmet-async'
import { Route, Routes } from 'react-router-dom'

import { AppLoader } from './app/components/AppLoader'
import { AppProviders } from './app/components/AppProviders'
import { AuthRedirectHandler } from './app/components/AuthRedirectHandler'
import { Notification } from './app/components/Notification'
import { ProtectedRoute } from './app/components/ProtectedRoute'

import 'vanilla-cookieconsent/dist/cookieconsent.css'
import { LoginPage } from './app/modules/authentication/pages/Login'
import { MePage } from './app/modules/authentication/pages/Me'
import { OAuthConsentPage } from './app/modules/authentication/pages/OAuthConsent'
import { OnboardingPage } from './app/modules/onboarding/pages/Onboarding'
import { SignUpPage } from './app/modules/onboarding/pages/SignUp'
import AccessDeniedPage from './pages/AccessDeniedPage'
import AuthLoginPage from './pages/AuthLoginPage'
import AuthLogoutPage from './pages/AuthLogoutPage'
import NotFoundPage from './pages/NotFoundPage'

import { useEffect, useRef } from 'react'
import * as CookieConsent from 'vanilla-cookieconsent'
import { useLocaleInitialization } from './app/hooks/useLocaleInitialization'
import { ROUTES } from './app/lib/constants'
import {
  COOKIE_CONSENT_CONFIG,
  initializeDataLayerWithDefaultConsent,
} from './app/modules/shared/lib/cookieConsent'
import { loadGoogleTagManager } from './app/modules/shared/lib/analytics'

export function App() {
  const { isReady, locale, messages, changeLocale } = useLocaleInitialization()
  const isMounted = useRef(false)

  useEffect(() => {
    if (isMounted.current || !isReady) {
      return
    }

    initializeDataLayerWithDefaultConsent()
    loadGoogleTagManager()
    CookieConsent.run(COOKIE_CONSENT_CONFIG)

    isMounted.current = true
  }, [isReady])

  if (!isReady) {
    return <AppLoader />
  }

  return (
    <AppProviders locale={locale} messages={messages} changeLocale={changeLocale}>
      <AuthRedirectHandler />
      <Helmet>
        <title>Mydra</title>
      </Helmet>

      <Routes>
        <Route path={ROUTES.homepage} element={<LoginPage />} />
        <Route path={ROUTES.login} element={<LoginPage />} />
        <Route path={ROUTES.signup} element={<SignUpPage />} />
        <Route path={ROUTES.authLogin} element={<AuthLoginPage />} />
        <Route path={ROUTES.logout} element={<AuthLogoutPage />} />
        <Route path={ROUTES.accessDenied} element={<AccessDeniedPage />} />
        <Route path={ROUTES.notFound} element={<NotFoundPage />} />

        <Route
          path={ROUTES.me}
          element={
            <ProtectedRoute>
              <MePage />
            </ProtectedRoute>
          }
        />

        <Route
          path={ROUTES.onboarding}
          element={
            <ProtectedRoute>
              <OnboardingPage />
            </ProtectedRoute>
          }
        />

        <Route
          path={ROUTES.oauthConsent}
          element={
            <ProtectedRoute>
              <OAuthConsentPage />
            </ProtectedRoute>
          }
        />

        <Route path="*" element={<NotFoundPage />} />
      </Routes>

      <Notification />
    </AppProviders>
  )
}
